import type { DataSource, EntityCollection } from "@firecms/core";
import type { NotificationDef } from "../collections/notifications";
import {
  findDuplicateTypesInFile,
  validateNotificationDefImportItem,
} from "./validateNotificationDefImportItem";
import type {
  NotificationDefImportItem,
  NotificationDefImportResult,
} from "./types";

function normalizeItem(
  item: NotificationDefImportItem
): Pick<NotificationDef, "type" | "title" | "body"> {
  return {
    type: item.type,
    title: item.title,
    body: item.body,
  };
}

export async function upsertNotificationDefs(params: {
  dataSource: DataSource;
  collection: EntityCollection<NotificationDef>;
  path: string;
  items: NotificationDefImportItem[];
}): Promise<NotificationDefImportResult> {
  const { dataSource, collection, path, items } = params;

  const duplicateCheck = findDuplicateTypesInFile(items);
  if (!duplicateCheck.ok) {
    return {
      imported: 0,
      failed: [{ index: 0, key: "file", reason: duplicateCheck.reason }],
    };
  }

  const existing = await dataSource.fetchCollection({
    path,
    collection,
  });
  const existingIdByType = new Map<string, string>();
  for (const entity of existing) {
    const type = entity.values?.type;
    if (typeof type === "string") {
      existingIdByType.set(type, entity.id);
    }
  }

  const result: NotificationDefImportResult = { imported: 0, failed: [] };

  for (let index = 0; index < items.length; index++) {
    const raw = items[index];
    if (!raw) {
      continue;
    }
    const key = typeof raw.type === "string" ? raw.type : `index-${index}`;

    const validation = validateNotificationDefImportItem(raw);
    if (!validation.ok) {
      result.failed.push({ index, key, reason: validation.reason });
      continue;
    }

    const values = normalizeItem(raw);
    const existingId = existingIdByType.get(values.type);

    try {
      const saved = await dataSource.saveEntity({
        path,
        collection,
        values,
        status: existingId ? "existing" : "new",
        ...(existingId ? { entityId: existingId } : {}),
      });

      const savedId = saved?.id ?? existingId;
      if (!savedId) {
        result.failed.push({
          index,
          key,
          reason: "Save succeeded but no entity id returned",
        });
        continue;
      }

      existingIdByType.set(values.type, savedId);
      result.imported += 1;
    } catch (error: unknown) {
      const reason =
        error instanceof Error ? error.message : "Unknown save error";
      result.failed.push({ index, key, reason });
    }
  }

  return result;
}
